import React, { useEffect, useRef, useState } from 'react';
import { InboxConversation, ChatMessage } from '../hooks';
import { Send, Image, Smile, Paperclip } from 'lucide-react';
import { format } from 'timeago.js';
import EmojiPicker from 'emoji-picker-react';

interface ChatWindowProps {
    conversation: InboxConversation | null;
    messages: ChatMessage[];
    loading: boolean;
    onSendMessage: (text: string) => Promise<void>;
    sending: boolean;
}

const ChatWindow: React.FC<ChatWindowProps> = ({ conversation, messages, loading, onSendMessage, sending }) => {
    const [inputText, setInputText] = useState('');
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const emojiPickerRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, loading]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
                setShowEmojiPicker(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // Auto-focus when conversation changes or when loading finishes
    useEffect(() => {
        if (!loading && !sending && textareaRef.current) {
            textareaRef.current.focus();
        }
    }, [conversation?.id, loading, sending]);

    const handleSend = async (e?: React.FormEvent) => {
        e?.preventDefault();
        if (!inputText.trim() || sending) return;

        const text = inputText;
        // Don't clear immediately, wait for success

        setShowEmojiPicker(false);

        try {
            await onSendMessage(text);
            setInputText(''); // Clear ONLY on success

            // Refocus after successful send
            setTimeout(() => {
                textareaRef.current?.focus();
            }, 50);
        } catch (error) {
            console.error("Failed to send", error);
            // Input text remains, so user can retry
        }
    };

    const onEmojiClick = (emojiData: any) => {
        setInputText(prev => prev + emojiData.emoji);
    };

    if (!conversation) {
        return (
            <div className="flex-1 h-full flex flex-col items-center justify-center bg-gray-50/30 text-gray-500">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-6 animate-pulse">
                    <Send className="w-10 h-10 text-gray-300 ml-1" />
                </div>
                <h3 className="text-xl font-bold text-gray-700 mb-2">Your Messages</h3>
                <p className="text-sm font-medium text-gray-400">Select a conversation to start messaging</p>
            </div>
        );
    }

    return (
        <div className="flex-1 h-full flex flex-col bg-white relative">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-white z-10 shadow-sm/50">
                <div className="flex items-center gap-4">
                    <div className="relative">
                        <img
                            src={conversation.user_pic || "https://via.placeholder.com/40"}
                            alt={conversation.user_name}
                            className="w-10 h-10 rounded-full border border-gray-100"
                        />
                        <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></span>
                    </div>
                    <div>
                        <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
                            {conversation.user_name}
                        </h2>
                        <a href={`https://instagram.com/${conversation.user_name}`} target="_blank" rel="noreferrer" className="text-xs text-blue-500 hover:underline">
                            View on Instagram
                        </a>
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 md:p-6 bg-[#FAFAFA] space-y-4 custom-scrollbar">
                {loading ? (
                    <div className="h-full flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    </div>
                ) : (
                    messages.map((msg, index) => {
                        const isMe = msg.is_from_business;
                        const isNextFromSame = index < messages.length - 1 && messages[index + 1].is_from_business === isMe;
                        // Show avatar if it's NOT me, and it is the LAST message of a sequence (next is different or end of list)
                        const showAvatar = !isMe && !isNextFromSame;

                        return (
                            <div
                                key={msg.id}
                                className={`flex w-full ${isMe ? 'justify-end' : 'justify-start'} animate-in fade-in slide-in-from-bottom-2 duration-300 relative group`}
                            >
                                {!isMe && (
                                    <div className="w-8 shrink-0 mr-2 flex flex-col justify-end">
                                        {showAvatar && (
                                            <img
                                                src={conversation.user_pic}
                                                className="w-8 h-8 rounded-full border border-gray-100"
                                                alt=""
                                            />
                                        )}
                                    </div>
                                )}
                                <div className={`flex flex-col ${isMe ? 'items-end' : 'items-start'} max-w-[70%]`}>
                                    <div
                                        className={`
                        px-4 py-2 text-[15px] relative shadow-sm
                        ${isMe
                                                ? 'bg-blue-600 text-white rounded-2xl rounded-br-sm'
                                                : 'bg-white text-gray-800 border border-gray-100 rounded-2xl rounded-bl-sm'
                                            }
                    `}
                                    >
                                        <p className="whitespace-pre-wrap leading-relaxed">{msg.text}</p>
                                    </div>
                                    <span
                                        className={`text-[10px] mt-1 px-1 font-medium ${isMe ? 'text-gray-400' : 'text-gray-400'}`}
                                    >
                                        {msg.created_at ? format(msg.created_at.toDate()) : 'sending...'}
                                    </span>
                                </div>
                            </div>
                        );
                    })
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="p-4 border-t border-gray-100 bg-white relative">
                {/* Emoji Picker Popover */}
                {showEmojiPicker && (
                    <div ref={emojiPickerRef} className="absolute bottom-20 left-4 z-50 shadow-2xl rounded-xl border border-gray-100">
                        <EmojiPicker
                            onEmojiClick={onEmojiClick}
                            lazyLoadEmojis={true}
                            width={320}
                            height={400}
                        />
                    </div>
                )}

                <form onSubmit={handleSend} className="flex flex-col gap-2 max-w-4xl mx-auto">
                    <div className="flex items-end gap-2 bg-gray-50 border border-gray-200 rounded-3xl px-2 py-2 focus-within:ring-2 focus-within:ring-blue-500/10 focus-within:border-blue-500 transition-all shadow-sm">

                        <button
                            type="button"
                            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                            className={`p-2 rounded-full hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors ${showEmojiPicker ? 'text-blue-500 bg-blue-50' : ''}`}
                        >
                            <Smile size={24} />
                        </button>

                        <textarea
                            ref={textareaRef}
                            className="flex-1 bg-transparent border-none focus:outline-none text-gray-700 placeholder-gray-400 min-h-[44px] max-h-[120px] py-2.5 px-2 resize-none custom-scrollbar"
                            placeholder="Message..."
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                            disabled={sending}
                            rows={1}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSend();
                                }
                            }}
                        />

                        <button
                            type="submit"
                            disabled={(!inputText.trim() && !sending) || sending}
                            className={`
                        p-2.5 mb-0.5 rounded-full transition-all duration-200 flex items-center justify-center min-w-[42px] min-h-[42px]
                        ${(inputText.trim() || sending)
                                    ? 'bg-blue-600 text-white shadow-md hover:bg-blue-700 hover:shadow-lg'
                                    : 'bg-transparent text-gray-300 cursor-not-allowed'
                                }
                    `}
                        >
                            {sending ? (
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                                <Send size={20} className={(inputText.trim()) ? "ml-0.5" : ""} />
                            )}
                        </button>
                    </div>
                    <div className="text-[10px] text-gray-300 text-center font-medium">
                        LinkPlease Inbox
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ChatWindow;
